import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

export default function DiagramAdmin() {
  const [diagrams, setDiagrams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDiagram, setSelectedDiagram] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [diagramToDelete, setDiagramToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadDiagrams = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/diagrams');
      if (!response.ok) {
        throw new Error('Failed to load diagrams');
      }
      const data = await response.json();
      setDiagrams(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiagrams();
  }, []);

  const handleDelete = async (diagram) => {
    setDiagramToDelete(diagram);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!diagramToDelete) return;

    try {
      const response = await fetch(`/api/diagrams?filename=${diagramToDelete.filename}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete diagram');
      }

      // Remove from local state
      setDiagrams(prev => prev.filter(d => d.filename !== diagramToDelete.filename));
      setDeleteDialogOpen(false);
      setDiagramToDelete(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = (diagram) => {
    const link = document.createElement('a');
    link.href = diagram.url;
    link.download = diagram.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredDiagrams = diagrams.filter(diagram =>
    diagram.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    diagram.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading diagrams...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Cable Diagram Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDiagrams}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by SKU or filename..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {filteredDiagrams.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'No diagrams found matching your search.' : 'No diagrams uploaded yet.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredDiagrams.map((diagram) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={diagram.filename}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={diagram.url}
                  alt={`Cable diagram for ${diagram.sku}`}
                  sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                  onClick={() => setSelectedDiagram(diagram)}
                  style={{ cursor: 'pointer' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {diagram.sku}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {formatDate(diagram.timestamp)}
                  </Typography>
                  <Chip 
                    label={`${diagram.filename}`} 
                    size="small" 
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(diagram)}
                  >
                    Download
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(diagram)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Full-size image dialog */}
      <Dialog
        open={!!selectedDiagram}
        onClose={() => setSelectedDiagram(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedDiagram?.sku} - Cable Diagram
        </DialogTitle>
        <DialogContent>
          <img
            src={selectedDiagram?.url}
            alt={`Cable diagram for ${selectedDiagram?.sku}`}
            style={{ width: '100%', height: 'auto' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDiagram(null)}>Close</Button>
          {selectedDiagram && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(selectedDiagram)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Diagram</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the diagram for "{diagramToDelete?.sku}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 